// File: src/pages/NotificationsPage.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import StripeLogo from '../assets/Payment-Logos/stripe-logo.svg';

import { ReactComponent as UndrawMailbox } from '../assets/NoNotification.svg';

import {
  LuDollarSign,
  LuTicket,
  LuCalendarX2,
  LuCalendarCheck2,
  LuBellRing,
  LuSave,
  LuUserPlus,
  LuUserCheck,
  LuMessageSquare,
  LuCircleCheck,
  LuCircleX,
  LuRefreshCcw,
  LuPenLine,
} from "react-icons/lu";

/**************************************************************
 * Helpers
 **************************************************************/
function getMidnight(d) {
  const clone = new Date(d);
  clone.setHours(0, 0, 0, 0);
  return clone;
}

function getDateRangeLabel(dateString) {
  if (!dateString) return 'Previous';
  const date = new Date(dateString);
  const now = new Date();

  const diffInMs = getMidnight(now).getTime() - getMidnight(date).getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return 'This Week';
  return 'Previous';
}

function groupNotificationsByDate(notifs) {
  const groups = {};
  notifs.forEach((n) => {
    const label = getDateRangeLabel(n.createdAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(n);
  });
  const order = ['Today', 'Yesterday', 'This Week', 'Previous'];
  return order.filter((l) => groups[l]).map((l) => ({ label: l, items: groups[l] }));
}

/** Always show: "11/12/25 • 2:14 PM" */
function getTimestampOrDate(createdAt) {
  const dateObj = new Date(createdAt);

  const date = dateObj.toLocaleDateString([], {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });

  const time = dateObj.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${date} • ${time}`;
}

function initialsFromName(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  const a = parts[0]?.[0] || '';
  const b = parts[1]?.[0] || '';
  return (a + b).toUpperCase();
}

function safeImg(url) {
  if (!url) return '';
  if (String(url).startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL}${url}`;
}

// Extract ids we can use to enrich the row (optional, cached)
function getActorUserId(notif) {
  const t = String(notif?.type || '');
  if (t === 'chatRequest' || t === 'chatRequestAccepted' || t === 'chatRequestDeclined') {
    return notif?.metadata?.fromUserId || null;
  }
  if (t === 'newMessage') {
    return notif?.metadata?.fromUserId || null;
  }
  return null;
}

function authHeaders() {
  const t = localStorage.getItem('accessToken') || '';
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

function getEventIdFromNotif(notif) {
  const t = String(notif?.type || '');
  if (
    t === 'eventReminderOneHour' ||
    t === 'eventIsLive' ||
    t === 'eventSave' ||
    t === 'eventRegistered' ||
    t === 'eventRSVP' ||
    t === 'eventUpdated' ||
    t === 'eventCanceled' ||
    t === 'hostEventChangesRequested' ||
    t === 'hostEventRejected' ||
    t === 'hostEventApproved' ||
    t === 'hostTicketSold' ||
    t === 'hostTicketCanceled'
  ) {
    return notif?.metadata?.eventId || null;
  }
  return notif?.metadata?.eventId || null;
}

/**************************************************************
 * Page
 **************************************************************/
export default function NotificationsPage() {
  useEffect(() => {
    document.documentElement.classList.add('show-scrollbar');
    return () => document.documentElement.classList.remove('show-scrollbar');
  }, []);

  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  // Infinite scroll
  const [page, setPage] = useState(1);
  const limit = 10;
  const [hasMore, setHasMore] = useState(true);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  // Lightweight enrichment caches (optional)
  const [userById, setUserById] = useState({});   // { [id]: { name, profilePicUrl } }
  const [eventById, setEventById] = useState({}); // { [id]: { name, imageUrl } }

  const loaderRef = useRef(null);

  // Reset when user changes
  useEffect(() => {
    if (!currentUser?._id) return;
    setPage(1);
    setNotifications([]);
    setHasMore(true);
    setUserById({});
    setEventById({});
  }, [currentUser]);

  // Fetch when page changes
  useEffect(() => {
    if (!currentUser?._id) return;
    fetchNotifications(page);
    // eslint-disable-next-line
  }, [page]);

  // Intersection Observer
  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !loading) {
        setPage((prev) => prev + 1);
      }
    });
    observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [loaderRef, hasMore, loading]);

  async function fetchNotifications(currentPage) {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/notifications`, {
        params: { userId: currentUser._id, page: currentPage, limit },
      });
      const newData = res.data || [];
      if (newData.length < limit) setHasMore(false);

      setNotifications((prev) => {
        const existing = new Set(prev.map((p) => p._id));
        const filtered = newData.filter((n) => !existing.has(n._id));
        const merged = [...prev, ...filtered];
        merged.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return merged;
      });
    } catch (err) {
      console.error('[NotificationsPage] Error fetching =>', err);
    } finally {
      setLoading(false);
    }
  }

  // Clear all notifications (robust: tries bulk first, then falls back to per-id deletes)
  async function clearAllNotifications() {
    if (!currentUser?._id || !notifications.length || clearingAll) return;

    const ok = window.confirm('Clear all notifications?');
    if (!ok) return;

    setClearingAll(true);

    try {
      const base = `${process.env.REACT_APP_API_URL}/notifications`;

      // 1) Try common bulk endpoints first (if your backend supports them)
    await axios.delete(`${base}/clear`, authHeaders());


      // UI clear (regardless of which backend path succeeded)
      setNotifications([]);
      setHasMore(false);
      setPage(1);
    } catch (err) {
      console.error('[NotificationsPage] Clear all failed =>', err);
    } finally {
      setClearingAll(false);
    }
  }

  // Enrich: fetch missing actor users + events (batched, cached)
  useEffect(() => {
    if (!notifications.length) return;

    const missingUserIds = new Set();
    const missingEventIds = new Set();

    for (const n of notifications) {
      const uid = getActorUserId(n);
      if (uid && !userById[String(uid)]) missingUserIds.add(String(uid));

      const eid = getEventIdFromNotif(n);
      if (eid && !eventById[String(eid)]) missingEventIds.add(String(eid));
    }

    const userIds = Array.from(missingUserIds).slice(0, 12);
    const eventIds = Array.from(missingEventIds).slice(0, 12);

    if (!userIds.length && !eventIds.length) return;

    let cancelled = false;

    (async () => {
      try {
        // users
        if (userIds.length) {
          const results = await Promise.all(
            userIds.map(async (id) => {
              try {
                const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/users/${id}`);
                const name = `${data?.firstName || ''} ${data?.lastName || ''}`.trim();
                return {
                  id,
                  name: name || 'Someone',
                  profilePicUrl: safeImg(data?.profilePicUrl || ''),
                };
              } catch {
                return { id, name: 'Someone', profilePicUrl: '' };
              }
            })
          );
          if (!cancelled) {
            setUserById((prev) => {
              const next = { ...prev };
              for (const u of results) next[u.id] = { name: u.name, profilePicUrl: u.profilePicUrl };
              return next;
            });
          }
        }

        // events
        if (eventIds.length) {
          const results = await Promise.all(
            eventIds.map(async (id) => {
              try {
                const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/events/${id}`);
                const name = data?.name || data?.title || 'Event';
                const img =
                  (Array.isArray(data?.galleryImages) && data.galleryImages[0]) ||
                  (Array.isArray(data?.navigationImages) && data.navigationImages[0]?.url) ||
                  '';
                return { id, name, imageUrl: safeImg(img) };
              } catch {
  // Fallback for rejected / non-public events
  const snap =
    notifications.find(n => String(n?.metadata?.eventId) === String(id))
      ?.metadata?.submissionSnapshot;

  const img =
    (Array.isArray(snap?.galleryImages) && snap.galleryImages[0]) || '';

  return { id, name: 'Event', imageUrl: safeImg(img) };
}
            })
          );
          if (!cancelled) {
            setEventById((prev) => {
              const next = { ...prev };
              for (const e of results) next[e.id] = { name: e.name, imageUrl: e.imageUrl };
              return next;
            });
          }
        }
      } catch {
        // noop
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notifications, userById, eventById]);

  const markOneAsRead = async (notif) => {
    if (notif.isRead) return;
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/notifications/${notif._id}`, {
        isRead: true,
      });
      setNotifications((prev) =>
        prev.map((n) => (n._id === notif._id ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error('[NotificationsPage] Error marking single =>', err);
    }
  };

  const handleNotificationClick = async (notif) => {
    try {
      await markOneAsRead(notif);

      switch (notif.type) {
        case 'eventCanceled':
          navigate('/events');
          break;

        case 'eventRSVP':
        case 'eventUpdated':
        case 'eventReminderOneHour':
        case 'eventIsLive':
        case 'eventSave':
        case 'eventRegistered':
          if (notif.metadata?.eventId) {
            try {
              const resp = await axios.get(`${process.env.REACT_APP_API_URL}/events/${notif.metadata.eventId}`);
              if (resp.status === 200) navigate(`/events?focusEventId=${notif.metadata.eventId}`);
            } catch {
              // ignore
            }
          }
          break;

        case 'newMessage':
          if (notif.metadata?.chatRoomId) navigate(`/friends?roomId=${notif.metadata.chatRoomId}`);
          break;

        case 'chatRequest':
        case 'chatRequestDeclined':
          if (notif.metadata?.requestId) navigate(`/friends?focusRequestId=${notif.metadata.requestId}`);
          else navigate('/friends');
          break;

        case 'chatRequestAccepted':
          if (notif.metadata?.requestId) navigate(`/friends?tab=contacts&focusRequestId=${notif.metadata.requestId}`);
          else navigate('/friends?tab=contacts');
          break;

        default:
          // host notifications: mark-as-read only (routing is optional)
          break;
      }
    } catch (err) {
      console.error('[handleNotificationClick] =>', err);
    }
  };

  const hasNoNotifications = notifications.length === 0;
  const groupedNotifs = groupNotificationsByDate(notifications);

  // Build a Facebook-ish primary line
  const renderPrimaryLine = (notif) => {
    const t = String(notif?.type || '');
    const actorId = getActorUserId(notif);
    const actor = actorId ? userById[String(actorId)] : null;

    const evId = getEventIdFromNotif(notif);
    const ev = evId ? eventById[String(evId)] : null;

    const actorName = actor?.name || '';
    const eventName = ev?.name || '';

    if (t === 'newMessage') {
      return (
        <>
          <span style={ui.bold}>{actorName || 'New message'}</span>
          <span style={ui.normal}> sent you a message</span>
          {eventName ? <span style={ui.mutedInline}> · {eventName}</span> : null}
        </>
      );
    }

    if (t === 'chatRequest') {
      return (
        <>
          <span style={ui.bold}>{actorName || 'Someone'}</span>
          <span style={ui.normal}> wants to chat</span>
        </>
      );
    }

    if (t === 'chatRequestAccepted') {
      return (
        <>
          <span style={ui.bold}>{actorName || 'Someone'}</span>
          <span style={ui.normal}> accepted your chat request</span>
        </>
      );
    }

    if (t === 'eventReminderOneHour') {
      return (
        <>
          <span style={ui.bold}>{eventName || notif.title || 'Event reminder'}</span>
          <span style={ui.normal}> starts in 1 hour</span>
        </>
      );
    }

    if (t === 'eventIsLive') {
      return (
        <>
          <span style={ui.bold}>{eventName || notif.title || 'Event'}</span>
          <span style={ui.normal}> is live now</span>
        </>
      );
    }

    if (t === 'eventSave') {
      return (
        <>
          <span style={ui.bold}>Saved</span>
          <span style={ui.normal}> {eventName || notif.title || 'event'}</span>
        </>
      );
    }

    if (t === 'eventRegistered') {
      return (
        <>
          <span style={ui.bold}>Ticket confirmed</span>
          {eventName ? <span style={ui.normal}> · {eventName}</span> : null}
        </>
      );
    }

    if (t === 'eventCanceled') {
      return (
        <>
          <span style={ui.bold}>Event canceled</span>
          {eventName ? <span style={ui.normal}> · {eventName}</span> : null}
        </>
      );
    }

    // Host notifications
    if (t === 'hostEventChangesRequested') {
      return (
        <>
          <span style={ui.bold}>Changes requested</span>
          {eventName ? <span style={ui.normal}> · {eventName}</span> : null}
        </>
      );
    }

    if (t === 'hostEventRejected') {
      return (
        <>
          <span style={ui.bold}>Event rejected</span>
          {eventName ? <span style={ui.normal}> · {eventName}</span> : null}
        </>
      );
    }

    if (t === 'hostEventApproved') {
      return (
        <>
          <span style={ui.bold}>Event published</span>
          {eventName ? <span style={ui.normal}> · {eventName}</span> : null}
        </>
      );
    }

    if (t === 'hostTicketSold') {
      return (
        <>
          <span style={ui.bold}>Ticket sold</span>
          {eventName ? <span style={ui.normal}> · {eventName}</span> : null}
        </>
      );
    }

    if (t === 'hostTicketCanceled') {
      return (
        <>
          <span style={ui.bold}>Ticket canceled</span>
          {eventName ? <span style={ui.normal}> · {eventName}</span> : null}
        </>
      );
    }

if (t === 'hostPayoutSent') {
  const amtRaw = notif?.metadata?.amount;
  const amtNum =
    typeof amtRaw === 'number'
      ? amtRaw
      : (amtRaw != null ? Number(amtRaw) : NaN);

  const amt = Number.isFinite(amtNum) ? amtNum.toFixed(2) : '';

  return (
    <>
      <span style={ui.bold}>Payout sent</span>
      {amt && <span style={ui.normal}> · ${amt}</span>}
    </>
  );
}

    return <span style={ui.bold}>{notif.title || 'Notification'}</span>;
  };



  const badgeByType = {
  // 💸 PAYMENTS
  hostPayoutSent: {
    bg: '#16A34A', // green
    icon: LuDollarSign,
  },

  // 🎟️ TICKETS (HOST)
  hostTicketSold: {
    bg: '#2563EB', // blue
    icon: LuTicket,
  },

  hostTicketCanceled: {
    bg: '#DC2626', // red
    icon: LuCircleX,
  },

  // 📅 EVENT STATUS
  eventCanceled: {
    bg: '#DC2626',
    icon: LuCalendarX2,
  },

  eventRegistered: {
    bg: '#16A34A',
    icon: LuCalendarCheck2,
  },

  eventReminderOneHour: {
    bg: '#F59E0B', // amber
    icon: LuBellRing,
  },

  eventIsLive: {
    bg: '#DC2626', // red = live urgency
    icon: LuBellRing,
  },

  eventSave: {
    bg: '#64748B', // slate
    icon: LuSave,
  },

  eventUpdated: {
    bg: '#0EA5E9', // sky
    icon: LuRefreshCcw,
  },

  // 🧑‍🤝‍🧑 SOCIAL
  chatRequest: {
    bg: '#6366F1', // indigo
    icon: LuUserPlus,
  },

  chatRequestAccepted: {
    bg: '#16A34A',
    icon: LuUserCheck,
  },

  newMessage: {
    bg: '#2563EB',
    icon: LuMessageSquare,
  },

  // 🧑‍💼 HOST LIFECYCLE
  hostEventApproved: {
    bg: '#16A34A',
    icon: LuCircleCheck,
  },

  hostEventRejected: {
    bg: '#DC2626',
    icon: LuCircleX,
  },

  hostEventChangesRequested: {
    bg: '#F59E0B',
    icon: LuPenLine,
  },
};


  // Leading visual: prefer avatar, otherwise event image, otherwise initials dot.
  const renderLeadingVisual = (notif) => {
    
const t = String(notif?.type || '');

// ✅ Stripe payout icon (HOST PAYOUTS ONLY)
if (t === 'hostPayoutSent') {
  return (
    <div
      style={{
        ...ui.avatarWrap,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
      }}
      aria-hidden
    >
      <img
        src={StripeLogo}
        alt="Stripe payout"
        style={{ width: 36, height: 36 }}
      />

      <div style={{ ...ui.badgeWrap, background: '#16A34A' }}>
        <LuDollarSign style={ui.badgeIcon} color="#FFFFFF" />
      </div>
    </div>
  );
}


const actorId = getActorUserId(notif);
    const actor = actorId ? userById[String(actorId)] : null;

    const evId = getEventIdFromNotif(notif);
    const ev = evId ? eventById[String(evId)] : null;

    const img = actor?.profilePicUrl || ev?.imageUrl || '';

    const fallbackByType = {
      hostPayoutSent: 'P',
      hostTicketSold: 'T',
      hostTicketCanceled: 'T',
      hostEventApproved: 'H',
      hostEventRejected: 'H',
      hostEventChangesRequested: 'H',
      eventCanceled: 'E',
    };

    const fallbackText =
      actor?.name ? initialsFromName(actor.name)
      : ev?.name ? initialsFromName(ev.name)
      : (fallbackByType[t] || '');

  const badge = badgeByType[t];
const BadgeIcon = badge?.icon;

return (
  <div style={ui.avatarWrap} aria-hidden>
  <div style={ui.avatarClip}>
    {img ? (
      <img src={img} alt="" style={ui.avatarImg} />
    ) : (
      <div style={ui.avatarFallback}>{fallbackText}</div>
    )}
  </div>

  {badge && (
    <div style={{ ...ui.badgeWrap, background: badge.bg }}>
      <BadgeIcon style={ui.badgeIcon} color="#FFFFFF" />
    </div>
  )}
</div>

);
  };

  return (
    <div className="show-scrollbar" style={hasNoNotifications ? pageContainerEmptyStyle : ui.page}>
      <div style={hasNoNotifications ? notificationsContainerStyle : ui.container}>
        {/* Top bar */}
        <div style={ui.topBar}>
          {!hasNoNotifications ? (
            <button
              style={{ ...ui.clearAllBtn, ...(clearingAll ? ui.clearAllBtnDisabled : {}) }}
              onClick={clearAllNotifications}
              disabled={clearingAll}
              type="button"
            >
              Clear all
            </button>
          ) : null}
        </div>

        {/* 1) FIRST-LOAD SKELETON */}
        {loading && notifications.length === 0 ? (
          <section style={{ marginTop: 4 }}>
            <div style={ui.groupHeader}>
              <span style={{ ...ui.groupPill, visibility: 'hidden' }}>Today</span>
            </div>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} style={ui.itemContainer}>
                <div style={ui.itemRow}>
                  <div style={{ ...sk.avatar }} className="shimmer" />
                  <div style={ui.textCol}>
                    <div style={ui.primaryLine}>
                      <div style={{ ...sk.line, width: '62%' }} className="shimmer" />
                    </div>
                    <div style={{ ...sk.line, width: '40%', marginTop: 8 }} className="shimmer" />
                  </div>
                  <span style={{ ...ui.unreadSpacer }} />
                </div>
                {i < 7 && <div style={ui.divider} />}
              </div>
            ))}
          </section>
        ) : hasNoNotifications ? (
          /* 2) EMPTY STATE */
          <div style={emptyStateWrapperStyle}>
            <div style={circleWrapperStyle}>
              <UndrawMailbox width={240} />
            </div>
            <div style={emptyStateTitleStyle}>No Notifications</div>
            <div style={emptyStateSubtitleStyle}>We'll let you know when something happens.</div>
            <button style={findEventsBtnStyle} onClick={() => navigate('/events')} type="button">
              Find Events
            </button>
          </div>
        ) : (
          /* 3) GROUPS */
          groupedNotifs.map((section) => (
            <section key={section.label} style={ui.groupBlock}>
              <div style={ui.groupHeader}>
                <span style={ui.groupPill}>{section.label}</span>
              </div>

              {section.items.map((notif, idx) => {
                const isUnread = !notif.isRead;

                return (
                  <div key={notif._id} style={ui.itemContainer}>
                    <div
                      onClick={() => handleNotificationClick(notif)}
                      style={{ ...ui.itemRow, ...(isUnread ? ui.itemUnread : {}) }}
                      role="button"
                      tabIndex={0}
                    >
                      {renderLeadingVisual(notif)}

                      <div style={ui.textCol}>
                        <div style={ui.primaryLine}>
                          <span style={ui.primaryText}>{renderPrimaryLine(notif)}</span>
                        </div>

                        {notif.message ? (
                          <div style={ui.secondaryText}>
                            {String(notif.message).replace(/\s+/g, ' ').trim()}
                          </div>
                        ) : null}

                        {/* Date • time (no type bubbles) */}
                        <div style={ui.metaRow}>
                          <span style={ui.metaTime}>{getTimestampOrDate(notif.createdAt)}</span>
                        </div>
                      </div>

                      {isUnread && <span style={ui.unreadDot} />}
                    </div>

                    {idx < section.items.length - 1 && <div style={ui.divider} />}
                  </div>
                );
              })}
            </section>
          ))
        )}

        {/* Sentinel */}
        {hasMore && !hasNoNotifications && (
          <div ref={loaderRef} style={ui.loaderRef}>
            {loading && <div style={ui.spinner} />}
          </div>
        )}

        {!hasMore && notifications.length > 0 && <p style={ui.endOfList}>You’ve reached the end.</p>}
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .shimmer {
          background: linear-gradient(90deg, #f3f4f6 0%, #e9edf3 20%, #f3f4f6 40%);
          background-size: 200% 100%;
          animation: shimmer 1.2s ease-in-out infinite;
          border-radius: 999px;
        }
      `}</style>
    </div>
  );
}

/**************************************************************
 * UI Styles (Facebook-ish: avatar + feed copy, light chrome)
 **************************************************************/
const ui = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 860,
    margin: '0 auto',
    padding: '18px',
    minHeight: '100vh',
    boxSizing: 'border-box',
    fontFamily:
      'Open Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    background: '#FFFFFF',
    color: '#0B1020',
  },
  container: {
    width: 'min(640px, 100%)',
    margin: '0 auto',
  },

 topBar: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
},

  topBarTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: '#0B1020',
    letterSpacing: '-0.01em',
  },
  clearAllBtn: {
  background: 'transparent',
  border: 'none',
  padding: 0,
  marginLeft: 'auto', // 👈 THIS LINE
  color: '#3A4050',
  fontSize: 12.5,
  fontWeight: 900,
  cursor: 'pointer',
  textUnderlineOffset: 3,
},

  clearAllBtnDisabled: {
    opacity: 0.45,
    cursor: 'default',
    textDecoration: 'none',
  },

  /* Group */
  groupBlock: { marginBottom: 10 },
  groupHeader: { marginBottom: 8, display: 'flex', alignItems: 'center' },
  groupPill: {
    background: '#F7F8FA',
    border: '1px solid #EEF1F6',
    color: '#3A4050',
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
  },

  /* Rows */
  itemContainer: { width: '100%' },
  itemRow: {
    display: 'grid',
    gridTemplateColumns: '44px 1fr 12px',
    alignItems: 'start',
    gap: 10,
    padding: '12px 0',
    background: '#FFFFFF',
    cursor: 'pointer',
  },
  itemUnread: {},


  avatarWrap: {
  position: 'relative',
  width: 36,
  height: 36,
  flexShrink: 0,
  marginTop: 2,
},

avatarClip: {
  width: 36,
  height: 36,
  borderRadius: 999,
  overflow: 'hidden',
  border: '1px solid #E7E9EE',
  background: '#F7F8FA',
  display: 'grid',
  placeItems: 'center',
},


  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  avatarFallback: {
    width: '100%',
    height: '100%',
    display: 'grid',
    placeItems: 'center',
    fontSize: 12,
    fontWeight: 900,
    color: '#3A4050',
    letterSpacing: '0.02em',
  },

  textCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    paddingRight: 6,
  },

  primaryLine: {
    display: 'flex',
    alignItems: 'baseline',
    minWidth: 0,
  },
  primaryText: {
    fontSize: 13.5,
    lineHeight: 1.35,
    color: '#0B1020',
    overflowWrap: 'anywhere',
  },
  bold: { fontWeight: 900 },
  normal: { fontWeight: 600 },
  mutedInline: { color: '#6B7280', fontWeight: 700 },

  secondaryText: {
    marginTop: 4,
    fontSize: 12.5,
    lineHeight: 1.4,
    color: '#3A4050',
    overflowWrap: 'anywhere',
  },

  metaRow: {
    marginTop: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  metaTime: {
    fontSize: 11.5,
    fontWeight: 800,
    color: '#6B7280',
  },

badgeWrap: {
  position: 'absolute',
  bottom: -6,
  right: -2,
  width: 16,
  height: 16,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid #FFFFFF',
  zIndex: 2,
},

badgeIcon: {
  width: 10,
  height: 10,
},

  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#3B6BFF',
    justifySelf: 'end',
    marginTop: 12,
  },
  unreadSpacer: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: 'transparent',
    justifySelf: 'end',
    marginTop: 12,
  },

  divider: {
    height: 1,
    backgroundColor: '#EEF0F2',
    marginLeft: 46,
  },

  /* Loader & end */
  loaderRef: { display: 'flex', justifyContent: 'center', padding: '12px 0' },
  spinner: {
    width: 18,
    height: 18,
    border: '3px solid #E7E9EE',
    borderTop: '3px solid #555',
    borderRadius: '50%',
    animation: 'spin .8s linear infinite',
  },
  endOfList: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: 12.5,
    marginTop: 10,
  },
};

/**************************************************************
 * Empty state styles (kept)
 **************************************************************/
const pageContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '450px',
  margin: '0 auto',
  padding: '1rem',
  boxSizing: 'border-box',
  fontFamily: "'Open Sans', sans-serif",
  minHeight: '100vh',
  backgroundColor: '#fff',
  overflow: 'auto',
};

const pageContainerEmptyStyle = {
  ...pageContainerStyle,
  height: '100vh',
  overflow: 'hidden',
};

const notificationsContainerStyle = {
  marginBottom: '1rem',
};

const emptyStateWrapperStyle = {
  display: 'flex',
  marginBottom: '50%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  textAlign: 'center',
};

const circleWrapperStyle = {
  borderRadius: '50%',
};

const emptyStateTitleStyle = {
  fontSize: '1rem',
  fontWeight: 700,
  marginBottom: '0.35rem',
  color: '#0f172a',
  letterSpacing: '-0.01em',
};

const emptyStateSubtitleStyle = {
  fontSize: '.88rem',
  color: '#6b7280',
  maxWidth: '240px',
  lineHeight: 1.45,
  margin: '0 auto',
};

const findEventsBtnStyle = {
  background: '#ddd',
  color: '#555',
  border: '1px solid #ddd',
  borderRadius: 10,
  padding: '12px 18px',
  fontSize: 12,
  fontWeight: 900,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  marginTop: '1.5rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'filter .15s ease',
  
};


/**************************************************************
 * Skeleton tokens
 **************************************************************/
const sk = {
  line: {
    height: 10,
    background: '#f3f4f6',
    borderRadius: 6,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: '1px solid #E7E9EE',
    background: '#F7F8FA',
  },
  
};

